/**
 * V33.1: 휴가 잔액 조회 훅 - CF 전환
 *
 * **변경 사항:**
 * - Firestore direct read → Cloud Function 호출
 * - permission-denied 에러 해결
 * - 캐시 우선 조회로 빠른 응답
 *
 * **기존 인터페이스 유지:**
 * - useMyLeaveBalance(): { balance, isLoading, error }
 * - useLeaveBalance(employeeId): { balance, isLoading, error }
 * - useTeamLeaveBalances(employeeIds): { balances, isLoading, error }
 * - useAllLeaveBalances(): { balances, isLoading, error }
 */

import { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import { LeaveBalance } from '../types/leaveBalance';
import { useCurrentUser } from './useCurrentUser';

// ===== CF Response Types =====

interface LeaveBalanceCFResponse {
  success: boolean;
  data?: {
    id: string;
    employeeId: string;
    employeeName?: string;
    // V44: 내 휴가현황 테이블용 사용자 기본 정보
    employeeNumber?: string;
    orgUnitId?: string;
    orgUnitName?: string;
    annual?: {
      granted: number;
      used: number;
      remaining: number;
      carryOver: number;
      negative: number;
      // V44: 선사용, 적용기간, 월별사용량 추가
      preUsed?: number;
      validFrom?: string;
      validUntil?: string;
      periodStart?: string;
      periodEnd?: string;
      monthlyUsage?: Record<number, number>;
    };
    compensatory?: {
      granted: number;
      used: number;
      remaining: number;
    };
    personalHealing?: {
      q1Used: boolean;
      q2Used: boolean;
      q3Used: boolean;
      q4Used: boolean;
      // V44: 분기별 사용량 추가
      quarterlyUsage?: Record<number, number>;
    };
    // V69: CN 특수휴가 3종 (womenday/birthday/healthcn) — 정규직 전용, 적용기간 1회
    cnSpecialLeaves?: {
      womenday?: CnSpecialLeaveEntry;
      birthday?: CnSpecialLeaveEntry;
      healthcn?: CnSpecialLeaveEntry;
    } | null;
    updatedAt?: string;
  };
  cacheStatus?: 'hit' | 'miss' | 'refreshed';
  error?: string;
}

// V69: CN 특수휴가 entry
export interface CnSpecialLeaveEntry {
  usedInPeriod: boolean;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  nextResetDate: string;
  lastUsedAt?: string;
}

// ===== Helpers =====

/**
 * CF 응답을 LeaveBalance 타입으로 변환
 *
 * **주의**: CF에서 updatedAt은 ISO string으로 반환됨
 * LeaveBalance.updatedAt은 Timestamp 타입이지만, FE에서는 Date로 사용해도 무방
 * (toDate() 호출 시 안전하게 처리)
 */
/**
 * V44: CF 응답을 LeaveBalance + 확장 필드로 변환
 *
 * 확장 필드 (LeaveBalance 타입 외):
 * - employeeNumber: 사번
 * - orgUnitId: 조직 ID
 * - orgUnitName: 조직명
 */
export interface ExtendedLeaveBalance extends LeaveBalance {
  employeeNumber?: string;
  orgUnitId?: string;
  orgUnitName?: string;
  // V69: CN 특수휴가 3종
  cnSpecialLeaves?: {
    womenday?: CnSpecialLeaveEntry;
    birthday?: CnSpecialLeaveEntry;
    healthcn?: CnSpecialLeaveEntry;
  } | null;
}

function mapCFResponseToLeaveBalance(data: LeaveBalanceCFResponse['data']): ExtendedLeaveBalance | null {
  if (!data) return null;

  // updatedAt을 Timestamp-like 객체로 변환 (toDate 메서드 포함)
  const updatedAtDate = data.updatedAt ? new Date(data.updatedAt) : new Date();
  const updatedAtTimestampLike = {
    seconds: Math.floor(updatedAtDate.getTime() / 1000),
    nanoseconds: (updatedAtDate.getTime() % 1000) * 1000000,
    toDate: () => updatedAtDate,
    toMillis: () => updatedAtDate.getTime(),
    isEqual: (other: any) => other?.toMillis?.() === updatedAtDate.getTime(),
  };

  return {
    id: data.id,
    employeeId: data.employeeId,
    employeeName: data.employeeName || '',
    // V44: 사용자 기본 정보
    employeeNumber: data.employeeNumber,
    orgUnitId: data.orgUnitId,
    orgUnitName: data.orgUnitName,
    // V44: annual 필드 확장 - preUsed, 적용기간, 월별사용량 포함
    annual: {
      granted: data.annual?.granted || 0,
      used: data.annual?.used || 0,
      remaining: data.annual?.remaining || 0,
      carryOver: data.annual?.carryOver || 0,
      negative: data.annual?.negative || 0,
      preUsed: data.annual?.preUsed,
      validFrom: data.annual?.validFrom,
      validUntil: data.annual?.validUntil,
      periodStart: data.annual?.periodStart,
      periodEnd: data.annual?.periodEnd,
      monthlyUsage: data.annual?.monthlyUsage,
    },
    compensatory: data.compensatory || {
      granted: 0,
      used: 0,
      remaining: 0,
    },
    // V44: personalHealing에 quarterlyUsage 포함
    personalHealing: {
      q1Used: data.personalHealing?.q1Used || false,
      q2Used: data.personalHealing?.q2Used || false,
      q3Used: data.personalHealing?.q3Used || false,
      q4Used: data.personalHealing?.q4Used || false,
      quarterlyUsage: data.personalHealing?.quarterlyUsage,
    },
    // V69: CN 특수휴가 3종 (정규직 전용, 적용기간 1회)
    cnSpecialLeaves: data.cnSpecialLeaves ?? null,
    updatedAt: updatedAtTimestampLike,
  } as ExtendedLeaveBalance;
}

// ===== Hooks =====

/**
 * V33.1 → V44: 내 휴가 잔액 조회 (CF 호출)
 *
 * **변경 전:** Firestore getDoc(leaveBalances/{uid})
 * **변경 후:** CF getMyLeaveBalance()
 *
 * **V44 확장:**
 * - ExtendedLeaveBalance 반환 (employeeNumber, orgUnitId, orgUnitName 포함)
 * - annual에 preUsed, validFrom/validUntil, periodStart/periodEnd, monthlyUsage 포함
 * - personalHealing에 quarterlyUsage 포함
 */
export function useMyLeaveBalance() {
  const { user } = useCurrentUser();
  const [balance, setBalance] = useState<ExtendedLeaveBalance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user?.profile?.id) {
      setIsLoading(false);
      return;
    }

    const fetchBalance = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const functions = getFunctions();
        const getMyLeaveBalanceCF = httpsCallable<void, LeaveBalanceCFResponse>(
          functions,
          'getMyLeaveBalance'
        );

        const result = await getMyLeaveBalanceCF();

        if (!result.data.success) {
          throw new Error(result.data.error || '휴가 잔액 조회 실패');
        }

        const mappedBalance = mapCFResponseToLeaveBalance(result.data.data);
        setBalance(mappedBalance);
      } catch (err: any) {
        console.error('휴가 잔액 조회 실패:', err);
        setError(new Error(err.message || '휴가 잔액 조회 중 오류가 발생했습니다.'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchBalance();
  }, [user?.profile?.id]);

  return { balance, isLoading, error };
}

/**
 * V33.1: 특정 직원 휴가 잔액 조회 (CF 호출)
 *
 * **변경 전:** Firestore getDoc(leaveBalances/{employeeId})
 * **변경 후:** CF getEmployeeLeaveBalance({ employeeId })
 */
export function useLeaveBalance(employeeId?: string) {
  const [balance, setBalance] = useState<LeaveBalance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!employeeId) {
      setIsLoading(false);
      return;
    }

    const fetchBalance = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const functions = getFunctions();
        const getEmployeeLeaveBalanceCF = httpsCallable<
          { employeeId: string },
          LeaveBalanceCFResponse
        >(functions, 'getEmployeeLeaveBalance');

        const result = await getEmployeeLeaveBalanceCF({ employeeId });

        if (!result.data.success) {
          throw new Error(result.data.error || '휴가 잔액 조회 실패');
        }

        const mappedBalance = mapCFResponseToLeaveBalance(result.data.data);
        setBalance(mappedBalance);
      } catch (err: any) {
        console.error('휴가 잔액 조회 실패:', err);
        setError(new Error(err.message || '휴가 잔액 조회 중 오류가 발생했습니다.'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchBalance();
  }, [employeeId]);

  return { balance, isLoading, error };
}

/**
 * V33.1: 팀원 휴가 잔액 목록 조회 (CF 호출)
 *
 * **변경 전:** 각 employeeId별 Firestore getDoc
 * **변경 후:** 각 employeeId별 CF getEmployeeLeaveBalance
 */
export function useTeamLeaveBalances(employeeIds: string[]) {
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (employeeIds.length === 0) {
      setBalances([]);
      setIsLoading(false);
      return;
    }

    const fetchBalances = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const functions = getFunctions();
        const getEmployeeLeaveBalanceCF = httpsCallable<
          { employeeId: string },
          LeaveBalanceCFResponse
        >(functions, 'getEmployeeLeaveBalance');

        const balancePromises = employeeIds.map(async (empId) => {
          try {
            const result = await getEmployeeLeaveBalanceCF({ employeeId: empId });
            if (result.data.success) {
              return mapCFResponseToLeaveBalance(result.data.data);
            }
            return null;
          } catch {
            return null;
          }
        });

        const results = await Promise.all(balancePromises);
        const validBalances = results.filter((b): b is LeaveBalance => b !== null);

        setBalances(validBalances);
      } catch (err: any) {
        console.error('팀 휴가 잔액 조회 실패:', err);
        setError(new Error(err.message || '팀 휴가 잔액 조회 중 오류가 발생했습니다.'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchBalances();
  }, [employeeIds.join(',')]);

  return { balances, isLoading, error };
}

/**
 * V33.1: 전체 직원 휴가 잔액 목록 조회 (HR용)
 *
 * **TODO:** 현재는 Firestore direct read 유지
 * - 이 훅은 HR 페이지에서만 사용됨
 * - HR 권한이 있으면 rules에서 허용됨
 * - 전체 목록 조회용 CF 추가 필요 시 별도 구현
 *
 * **임시 조치:** 기존 방식 유지 + TODO 표시
 *
 * **V197:** refetch 함수 추가 - 임포트 후 목록 갱신용
 */
export function useAllLeaveBalances() {
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchBalances = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // TODO: [V33.1] HR 전용 전체 조회 CF 구현 시 전환 필요
      // 현재는 Firestore direct read 사용 (HR 권한 필요)
      const { collection, getDocs, query } = await import('firebase/firestore');
      const { getDb } = await import('../../firebase/utils');

      const q = query(collection(getDb(), 'leaveBalances'));
      const snapshot = await getDocs(q);

      const balanceList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as LeaveBalance[];

      setBalances(balanceList);
    } catch (err: any) {
      console.error('전체 휴가 잔액 조회 실패:', err);
      setError(new Error(err.message || '전체 휴가 잔액 조회 중 오류가 발생했습니다.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBalances();
  }, [fetchBalances]);

  // V197: refetch 함수 노출
  const refetch = useCallback(() => {
    fetchBalances();
  }, [fetchBalances]);

  return { balances, isLoading, error, refetch };
}

/**
 * V33.11: 수동 잔액 새로고침 훅 (forceRefresh 지원)
 *
 * 잔액 변경 후 UI 즉시 반영이 필요한 경우 사용
 * - 휴가 승인/취소 직후
 * - 휴가 신청 성공 직후
 *
 * V33.11: forceRefresh=true로 캐시 강제 무효화
 */
export function useRefreshLeaveBalance() {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refreshMyBalance = useCallback(async (): Promise<LeaveBalance | null> => {
    try {
      setIsRefreshing(true);
      setError(null);

      const functions = getFunctions();
      // V33.11: forceRefresh 파라미터 추가
      const getMyLeaveBalanceCF = httpsCallable<{ forceRefresh?: boolean }, LeaveBalanceCFResponse>(
        functions,
        'getMyLeaveBalance'
      );

      // V33.11: forceRefresh=true로 캐시 강제 무효화
      const result = await getMyLeaveBalanceCF({ forceRefresh: true });

      if (!result.data.success) {
        throw new Error(result.data.error || '휴가 잔액 새로고침 실패');
      }

      return mapCFResponseToLeaveBalance(result.data.data);
    } catch (err: any) {
      console.error('휴가 잔액 새로고침 실패:', err);
      setError(new Error(err.message));
      return null;
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  return { refreshMyBalance, isRefreshing, error };
}
